import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getUserPanels,
  getPanel,
  createPanel,
  updatePanel,
  deletePanel,
} from "../../services/buttonPanelService";

export const buttonPanelKeys = {
  all: ["buttonPanels"],
  list: () => ["buttonPanels", "list"],
  detail: (panelId) => ["buttonPanels", "detail", panelId],
};

export function useButtonPanels(options = {}) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: buttonPanelKeys.list(),
    queryFn: () => getUserPanels(navigate),
    staleTime: 0,
    refetchOnMount: true,
    ...options,
    refetchOnWindowFocus: false,
  });
}

export function useButtonPanel(panelId, options = {}) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: buttonPanelKeys.detail(panelId),
    queryFn: () => getPanel(panelId, navigate),
    enabled: !!panelId,
    staleTime: 0,
    refetchOnMount: true,
    ...options,
  });
}

export function useCreateButtonPanel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => createPanel(payload, navigate),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: buttonPanelKeys.list() });
    },
  });
}

export function useUpdateButtonPanel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ panelId, payload }) => updatePanel(panelId, payload, navigate),
    onSuccess: (_data, variables) => {
      queryClient.refetchQueries({ queryKey: buttonPanelKeys.list() });
      queryClient.refetchQueries({ queryKey: buttonPanelKeys.detail(variables.panelId) });
    },
  });
}

export function useDeleteButtonPanel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (panelId) => deletePanel(panelId, navigate),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: buttonPanelKeys.list() });
    },
  });
}

export default useButtonPanels;
